import type { MetaFunction } from "react-router";
import { marketingLinks, MarketingShell, socialImageMeta } from "../components/marketing/layout";
import { ArrowRight, Check } from "../components/marketing/icons";
import { SITE_ORIGIN } from "../brand";

const APP_STORE_URL = "https://apps.shopify.com/quote-desk";
const PAGE_URL = `${SITE_ORIGIN}/pricing`;

export const links = marketingLinks;

export const meta: MetaFunction = () => [
  { title: "Quote Desk Pricing for Shopify B2B Workflows" },
  {
    name: "description",
    content:
      "Compare Quote Desk plans for Shopify B2B workflows, including shareable quotes, draft orders, bulk tools, PO parsing, and counter-offers.",
  },
  {
    name: "keywords",
    content:
      "Shopify B2B quotes pricing, AI counter-offer Shopify, PO PDF parser Shopify, B2B Shopify app cost, draftOrder Shopify wholesale",
  },
  { property: "og:title", content: "AI B2B Quote Desk Pricing — Free plan, then $29 to $79" },
  {
    property: "og:description",
    content: "Free B2B quote workflow for Shopify Basic/Grow/Advanced merchants — upgrade for unlimited quotes, PO parsing, and AI negotiation. 14-day trial on every paid tier.",
  },
  { property: "og:type", content: "website" },
  { property: "og:url", content: PAGE_URL },
  ...socialImageMeta(),
  { tagName: "link", rel: "canonical", href: PAGE_URL },
  {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "AI B2B Quote Desk + PO Parser",
      description:
        "B2B quotes, negotiated per-quote pricing, PO PDF parsing, and AI counter-offers for Shopify Basic, Grow, and Advanced merchants. No Shopify Plus required.",
      offers: [
        {
          "@type": "Offer",
          name: "Free",
          price: "0",
          priceCurrency: "USD",
          description:
            "Full quote → accept → draft-order workflow, up to 5 quotes every 30 days. Free forever.",
          availability: "https://schema.org/InStock",
        },
        {
          "@type": "Offer",
          name: "Starter",
          price: "29",
          priceCurrency: "USD",
          description:
            "Everything in Free + unlimited quotes + CSV bulk import/export. 14-day free trial.",
          availability: "https://schema.org/InStock",
        },
        {
          "@type": "Offer",
          name: "Pro",
          price: "49",
          priceCurrency: "USD",
          description:
            "Everything in Starter + PO PDF parsing + AI price suggestions from buyer order history. 14-day free trial.",
          availability: "https://schema.org/InStock",
        },
        {
          "@type": "Offer",
          name: "Growth",
          price: "79",
          priceCurrency: "USD",
          description:
            "Everything in Pro + AI counter-offer engine + unlimited quotes + full B2B workflow including bulk operations. 14-day free trial.",
          availability: "https://schema.org/InStock",
        },
      ],
    },
  },
];

interface Plan {
  name: string;
  price: string;
  cadence: string;
  tagline: string;
  features: { head?: string; items: string[] };
  ctaLabel: string;
  highlight?: boolean;
}

const PLANS: Plan[] = [
  {
    name: "Free",
    price: "$0",
    cadence: "/ month",
    tagline: "Run your real B2B quotes free — up to 5 every 30 days.",
    features: {
      items: [
        "Full quote → accept → draft order workflow",
        "Company directory for repeat B2B buyers",
        "Negotiated per-quote line pricing",
        "Public quote links (/q/<token>) — no buyer login",
        "Branded quote PDFs",
        "Up to 5 quotes every 30 days",
      ],
    },
    ctaLabel: "Install free",
  },
  {
    name: "Starter",
    price: "$29",
    cadence: "/ month",
    tagline: "Unlimited quotes and bulk tools, without Shopify Plus.",
    features: {
      head: "Everything in Free, plus",
      items: [
        "Unlimited quotes — no monthly cap",
        "CSV bulk import + filtered export",
      ],
    },
    ctaLabel: "Start 14-day free trial",
  },
  {
    name: "Pro",
    price: "$49",
    cadence: "/ month",
    tagline: "Add PO PDF parsing — turn an inbound PO into a draft order.",
    features: {
      head: "Everything in Starter, plus",
      items: [
        "PO PDF → draft order parsing",
        "Confidence-scored line matching to your catalog",
        "Review-before-send on every parsed line",
        "Bulk PO handling",
      ],
    },
    ctaLabel: "Start 14-day free trial",
    highlight: true,
  },
  {
    name: "Growth",
    price: "$79",
    cadence: "/ month",
    tagline: "Add the AI counter-offer engine and the verification harness.",
    features: {
      head: "Everything in Pro, plus",
      items: [
        "AI counter-offer engine from order history",
        "Margin-aware suggestions you approve before sending",
        "Verification harness — replay pricing against past orders",
        "Priority support",
      ],
    },
    ctaLabel: "Start 14-day free trial",
  },
];

const FAQ = [
  {
    q: "Is there a free plan?",
    a: "Yes. The Free plan runs the full quote → accept → draft-order workflow — company directory, public quote links, negotiated pricing, and branded PDFs — for up to 5 quotes every 30 days, free forever. Upgrade to Starter ($29/mo) when you need unlimited quotes and CSV bulk tools.",
  },
  {
    q: "What happens when I hit the free 5-quote limit?",
    a: "Your existing quotes stay fully editable and your buyers can still accept them — only creating a new quote is paused until your 30-day window rolls over or you upgrade to Starter for unlimited quotes.",
  },
  {
    q: "Do I need Shopify Plus to use this?",
    a: "No — that's the whole point. Quote Desk gives Shopify Basic, Grow, and Advanced merchants the B2B quote + PO-parsing + AI counter-offer workflow that Shopify normally reserves for the Plus tier.",
  },
  {
    q: "What does the free trial cover?",
    a: "Every paid plan includes 14 days of full access before billing starts — cancel anytime in your Shopify admin under Apps > Subscriptions and you're not charged. If you cancel or let a trial lapse, you drop to the Free plan rather than losing access.",
  },
  {
    q: "What happens when a buyer accepts a quote?",
    a: "The buyer opens the public quote link (/q/<token>) you shared, clicks Accept, and a real Shopify order is created via the draftOrderCreate GraphQL mutation. They pay through your normal Shopify checkout.",
  },
  {
    q: "How does PO PDF parsing work?",
    a: "On Pro and above, drop a PO PDF into the dashboard. Quote Desk extracts line items, quantities, and prices, and matches them to your catalog with a confidence score per line. You review before anything becomes a draft order.",
  },
  {
    q: "What is the AI counter-offer engine?",
    a: "On Growth, when a buyer responds with a counter-offer, the engine reads that company's order history and your margin floor and drafts a reasonable counter-counter. You approve, edit, or override before it sends — it never sends on your behalf.",
  },
];

export default function PricingPage() {
  return (
    <MarketingShell>
      <section className="qd-hero" style={{ paddingBottom: "1rem" }}>
        <div className="qd-wrap">
          <div className="qd-head qd-head--center qd-reveal" data-d="1">
            <p className="qd-eyebrow qd-eyebrow--center">Pricing · no setup fees · cancel anytime</p>
            <h2 style={{ fontSize: "var(--step-4)" }}>
              One engine. Four plans. Start free.
            </h2>
            <p>
              The whole B2B quote workflow runs on Shopify Basic, Grow, or
              Advanced — no Plus required. Start free with up to 5 quotes a
              month, then add unlimited volume, PO parsing, and the AI
              negotiation engine as you grow.
            </p>
          </div>
        </div>
      </section>

      <section className="qd-section--tight">
        <div className="qd-wrap">
          <div className="qd-plans" style={{ marginTop: "1rem" }}>
            {PLANS.map((plan, i) => (
              <div
                key={plan.name}
                className={`qd-plan qd-reveal ${plan.highlight ? "qd-plan--featured" : ""}`}
                data-d={String(i + 1)}
              >
                {plan.highlight ? <span className="qd-plan__flag">Most popular</span> : null}
                <div className="qd-plan__name">{plan.name}</div>
                <p className="qd-plan__tagline">{plan.tagline}</p>
                <div className="qd-plan__price">
                  <span className="qd-plan__amount">{plan.price}</span>
                  <span className="qd-plan__cadence">{plan.cadence}</span>
                </div>
                <p className="qd-plan__trial">{plan.name === "Free" ? "Free forever · no card" : "14-day free trial"}</p>
                <div className="qd-plan__cta">
                  <a
                    className={`qd-btn ${plan.highlight ? "" : "qd-btn--dark"}`}
                    href={APP_STORE_URL}
                  >
                    {plan.ctaLabel}
                    <ArrowRight />
                  </a>
                </div>
                <ul className="qd-plan__list">
                  {plan.features.head ? (
                    <li className="is-head">{plan.features.head}</li>
                  ) : null}
                  {plan.features.items.map((f) => (
                    <li key={f}>
                      <Check />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p
            style={{
              textAlign: "center",
              marginTop: "1.8rem",
              color: "var(--ink-500)",
              fontSize: "0.9rem",
            }}
          >
            Billed through Shopify. Upgrade or downgrade anytime — Shopify
            pro-rates the change. Month-to-month, no annual contract.
          </p>
        </div>
      </section>

      <section className="qd-section qd-band">
        <div className="qd-wrap qd-wrap--narrow">
          <div className="qd-head qd-head--center qd-reveal" data-d="1">
            <p className="qd-eyebrow qd-eyebrow--center">Common questions</p>
            <h2>Pricing, answered</h2>
          </div>
          <div className="qd-faq">
            {FAQ.map((item) => (
              <details key={item.q}>
                <summary>{item.q}</summary>
                <div className="qd-faq__body">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="qd-section">
        <div className="qd-wrap">
          <div className="qd-cta qd-reveal" data-d="1">
            <h2>Start free today</h2>
            <p>
              Install free and run the full quote workflow for up to 5 quotes a
              month — no card required. Upgrade any time for unlimited volume,
              PO parsing, and AI negotiation, each on a 14-day trial.
            </p>
            <div className="qd-cta__actions">
              <a className="qd-btn" href={APP_STORE_URL}>
                See it on the App Store
                <ArrowRight />
              </a>
              <a className="qd-btn qd-btn--ghost" href="/features">
                Compare features
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
